// Tiny path-based router for the sales-intelligence app. We avoid pulling
// in a full router library because the entire app has a fixed, very small
// set of top-level views.

import type { RangeMode, TimeRange } from "./api";

// Ticket 18 — Teams view toggles between By-team and By-assessment
// modes. The selected mode round-trips through the URL as ``vm`` so
// browser back/forward and deep links restore the right surface.
export type ViewMode = "team" | "assessment";

export type NavKey =
  | "intelligence"
  | "dialpad"
  // Wave 9 — super-admin-only Data view housing the heavy sync/data
  // tools moved out of the Dialpad view (Advanced Sync Call Data,
  // Dialpad Users, Assessment Runner, Delete Assessment Results).
  | "data"
  | "agents"
  | "agent-profile"
  | "call-detail"
  | "users"
  | "communities"
  | "teams"
  | "assessments"
  // TODO Ticket 2/3: `assessment-edit` is a placeholder nav key — the
  // edit/create sub-view ships in later tickets. AssessmentsView already
  // forwards row clicks here; until then deep links fall back to
  // `intelligence` via the App.tsx role gate.
  | "assessment-edit"
  // T2 — read-only assessment detail for admins & managers. The second
  // path segment carries the assessment id (reusing ``agentSlug`` like
  // `assessment-edit` does).
  | "assessment-view"
  | "search"
  // Wave 5 · T1 — Roundtable: multi-agent comparison Q&A. Visible to
  // all three roles (navItems.tsx); everything on the page is
  // session-only so the route carries no extra params.
  | "roundtable"
  // PET-641 — admin/supadmin editor for the Ask-a-Question preset
  // questions. Role-gated in navItems.tsx; deep links from other roles
  // bounce to Intelligence via the App.tsx visibleNavItems guard.
  | "manage-questions"
  // Wave 10 (port-plan 24) — admin/supadmin Generate Sales Scenarios
  // view. Role-gated in navItems.tsx; manager deep links bounce to
  // Intelligence via the App.tsx visibleNavItems guard.
  | "generate-scenarios"
  | "how-to-use";

export const BASE =
  (import.meta.env.BASE_URL || "/").replace(/\/$/, "") || "";

export type ParsedUrl = {
  nav: NavKey;
  agentSlug: string | null;
  agentKey: string | null;
  slice: string | null;
  team: string | null;
  callId: string | null;
  from: string | null;
  // Ticket 12/13 — only meaningful for nav='agent-profile'. Narrowed to
  // valid TimeRange / RangeMode at parse time so callers can use them
  // directly without re-validation.
  timeRange: TimeRange | null;
  rangeMode: RangeMode | null;
  // Task #365 — paired YYYY-MM-DD strings carried alongside
  // ``timeRange === 'custom'``. Both must be present for the custom
  // window to take effect; a half-specified pair is treated as "no
  // custom range" so a corrupt URL can't blow up the view.
  customStart: string | null;
  customEnd: string | null;
  // Ticket 18 — Teams view by-team / by-assessment toggle. Always
  // normalised to a valid ViewMode; defaults to "team" when absent.
  viewMode: ViewMode;
  // Task #290 — on nav='agent-profile', deep-link a focused measure so
  // the Assessment performance section opens with that bar pre-focused.
  // Reuses the same URL-state pattern as `slice`/`team` rather than
  // inventing a new mechanism. Both must be present to take effect.
  focusedMeasureId: string | null;
  focusedLabelName: string | null;
};

function _isValidTimeRange(v: string | null): boolean {
  return (
    v === "daily" ||
    v === "weekly" ||
    v === "monthly" ||
    v === "quarterly" ||
    v === "six_month" ||
    v === "all_time" ||
    v === "custom"
  );
}
function _isValidRangeMode(v: string | null): boolean {
  return v === "calendar" || v === "rolling";
}
// Task #365 — surface-level YYYY-MM-DD shape check for the ``start`` /
// ``end`` query params so a malformed URL degrades to "no custom
// range" rather than crashing the view. Backend re-validates strictly.
function _isValidIsoDate(v: string | null): boolean {
  if (!v) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime());
}

export function parseUrl(): ParsedUrl {
  if (typeof window === "undefined")
    return {
      nav: "intelligence",
      agentSlug: null,
      agentKey: null,
      slice: null,
      team: null,
      callId: null,
      from: null,
      timeRange: null,
      rangeMode: null,
      customStart: null,
      customEnd: null,
      viewMode: "team",
      focusedMeasureId: null,
      focusedLabelName: null,
    };
  const path = window.location.pathname;
  const rest = (BASE && path.startsWith(BASE) ? path.slice(BASE.length) : path)
    .replace(/^\//, "");
  const seg = rest.split("/").filter(Boolean);
  const sp = new URLSearchParams(window.location.search);
  const k = sp.get("k");
  const slice = sp.get("slice");
  const team = sp.get("team");
  const from = sp.get("from");
  const tr = sp.get("tr");
  const rm = sp.get("rm");
  const cs = sp.get("start");
  const ce = sp.get("end");
  const vm = sp.get("vm");
  const mid = sp.get("mid");
  const ln = sp.get("ln");
  // Default to "team" when absent or unrecognised so deep links can't
  // wedge the UI into an unknown mode.
  const viewMode: ViewMode = vm === "assessment" ? "assessment" : "team";
  const agentKey = k ? k : null;
  if (seg[0] === "calls" && seg[1])
    return {
      nav: "call-detail",
      agentSlug: null,
      agentKey: null,
      slice: null,
      team: null,
      callId: decodeURIComponent(seg[1]),
      from: from || null,
      timeRange: null,
      rangeMode: null,
      customStart: null,
      customEnd: null,
      viewMode: "team",
      focusedMeasureId: null,
      focusedLabelName: null,
    };
  if (seg[0] === "agents" && seg[1]) {
    // Task #290 — both ``mid`` and ``ln`` must be present for the
    // focused measure to take effect; a half-specified pair degrades
    // to "no focus" rather than crashing the view.
    const focusedMeasureId = mid && ln ? mid : null;
    const focusedLabelName = mid && ln ? ln : null;
    const apTimeRange = _isValidTimeRange(tr) ? (tr as TimeRange) : null;
    const apRangeMode = _isValidRangeMode(rm) ? (rm as RangeMode) : null;
    // Only carry the custom window when the active range is "custom"
    // *and* both bounds parse cleanly. A half-specified or malformed
    // pair degrades to "no custom range" so a corrupt URL can't crash.
    const apCustomStart =
      apTimeRange === "custom" && _isValidIsoDate(cs) && _isValidIsoDate(ce)
        ? cs
        : null;
    const apCustomEnd =
      apTimeRange === "custom" && _isValidIsoDate(cs) && _isValidIsoDate(ce)
        ? ce
        : null;
    return {
      nav: "agent-profile",
      agentSlug: seg[1],
      agentKey,
      slice: slice || null,
      team: team || null,
      callId: null,
      from: null,
      timeRange: apTimeRange,
      rangeMode: apRangeMode,
      customStart: apCustomStart,
      customEnd: apCustomEnd,
      viewMode: "team",
      focusedMeasureId,
      focusedLabelName,
    };
  }
  const empty = {
    agentSlug: null,
    agentKey: null,
    slice: null,
    team: null,
    callId: null,
    from: null,
    timeRange: null,
    rangeMode: null,
    customStart: null as string | null,
    customEnd: null as string | null,
    viewMode: "team" as ViewMode,
    focusedMeasureId: null,
    focusedLabelName: null,
  };
  if (seg[0] === "agents") {
    const agentsTimeRange = _isValidTimeRange(tr) ? (tr as TimeRange) : null;
    const agentsRangeMode = _isValidRangeMode(rm) ? (rm as RangeMode) : null;
    const agentsCustomStart =
      agentsTimeRange === "custom" && _isValidIsoDate(cs) && _isValidIsoDate(ce)
        ? cs
        : null;
    const agentsCustomEnd =
      agentsTimeRange === "custom" && _isValidIsoDate(cs) && _isValidIsoDate(ce)
        ? ce
        : null;
    return {
      nav: "agents",
      ...empty,
      timeRange: agentsTimeRange,
      rangeMode: agentsRangeMode,
      customStart: agentsCustomStart,
      customEnd: agentsCustomEnd,
    };
  }
  if (seg[0] === "dialpad") return { nav: "dialpad", ...empty };
  if (seg[0] === "data") return { nav: "data", ...empty };
  if (seg[0] === "users") return { nav: "users", ...empty };
  if (seg[0] === "manage-questions")
    return { nav: "manage-questions", ...empty };
  if (seg[0] === "communities") return { nav: "communities", ...empty };
  if (seg[0] === "teams") return { nav: "teams", ...empty };
  if (seg[0] === "assessments") return { nav: "assessments", ...empty };
  if (seg[0] === "assessment-edit") {
    // Second segment carries the assessment id (or "new"). Kept in
    // agentSlug so App.tsx can pass it straight into the editor without
    // adding a new state field. Hard refresh / deep-link preserves id.
    return {
      ...empty,
      nav: "assessment-edit",
      agentSlug: seg[1] ? decodeURIComponent(seg[1]) : null,
    };
  }
  if (seg[0] === "assessment-view") {
    // T2 — read-only detail (admins & managers). Second segment is the
    // assessment id, carried in agentSlug like the editor route.
    return {
      ...empty,
      nav: "assessment-view",
      agentSlug: seg[1] ? decodeURIComponent(seg[1]) : null,
    };
  }
  // T12 — the page formerly served at ``/clients`` now lives at
  // ``/search``. Legacy ``/clients`` URLs still parse to the Search view
  // so existing bookmarks land correctly; the address bar is normalised
  // to ``/search`` on load (see the legacy-redirect effect in App.tsx).
  if (seg[0] === "search" || seg[0] === "clients")
    return { nav: "search", ...empty };
  if (seg[0] === "roundtable") return { nav: "roundtable", ...empty };
  // `team` keeps the chosen team across a reload (ADR 0009: view state in the URL).
  if (seg[0] === "generate-scenarios")
    return { nav: "generate-scenarios", ...empty, team };
  if (seg[0] === "how-to-use") return { nav: "how-to-use", ...empty };
  // Ticket 18 — Teams view also round-trips `tr` / `rm` so deep
  // links like `/intelligence?vm=assessment&tr=monthly&rm=calendar` land
  // with the right mode + range, and toggling `vm` can preserve range.
  const intelTimeRange = _isValidTimeRange(tr) ? (tr as TimeRange) : null;
  const intelRangeMode = _isValidRangeMode(rm) ? (rm as RangeMode) : null;
  const intelCustomStart =
    intelTimeRange === "custom" && _isValidIsoDate(cs) && _isValidIsoDate(ce)
      ? cs
      : null;
  const intelCustomEnd =
    intelTimeRange === "custom" && _isValidIsoDate(cs) && _isValidIsoDate(ce)
      ? ce
      : null;
  if (seg[0] === "intelligence")
    return {
      nav: "intelligence",
      ...empty,
      viewMode,
      timeRange: intelTimeRange,
      rangeMode: intelRangeMode,
      customStart: intelCustomStart,
      customEnd: intelCustomEnd,
    };
  return {
    nav: "intelligence",
    ...empty,
    viewMode,
    timeRange: intelTimeRange,
    rangeMode: intelRangeMode,
    customStart: intelCustomStart,
    customEnd: intelCustomEnd,
  };
}

export function buildUrl(
  nav: NavKey,
  slug: string | null,
  key: string | null = null,
  opts?: {
    slice?: string | null;
    team?: string | null;
    callId?: string | null;
    from?: string | null;
    // Ticket 12/13: range pre-selection on agent-profile URLs.
    timeRange?: TimeRange | null;
    rangeMode?: RangeMode | null;
    // Task #365 — paired custom window (YYYY-MM-DD UTC). Both must be
    // supplied together to be written to the URL.
    customStart?: string | null;
    customEnd?: string | null;
    // Task #290 — focused measure on agent-profile URLs. Both must be
    // supplied together; either missing means "no focus" and the params
    // are omitted from the URL.
    focusedMeasureId?: string | null;
    focusedLabelName?: string | null;
  },
): string {
  const base = BASE || "";
  switch (nav) {
    case "intelligence": {
      const sp = new URLSearchParams();
      if (opts?.timeRange) sp.set("tr", opts.timeRange);
      if (opts?.rangeMode) sp.set("rm", opts.rangeMode);
      if (
        opts?.timeRange === "custom" &&
        opts?.customStart &&
        opts?.customEnd
      ) {
        sp.set("start", opts.customStart);
        sp.set("end", opts.customEnd);
      }
      const tail = sp.toString();
      return `${base}/${tail ? `?${tail}` : ""}`;
    }
    case "dialpad":
      return `${base}/dialpad`;
    case "data":
      return `${base}/data`;
    case "agents": {
      const sp = new URLSearchParams();
      if (opts?.timeRange) sp.set("tr", opts.timeRange);
      if (opts?.rangeMode) sp.set("rm", opts.rangeMode);
      if (
        opts?.timeRange === "custom" &&
        opts?.customStart &&
        opts?.customEnd
      ) {
        sp.set("start", opts.customStart);
        sp.set("end", opts.customEnd);
      }
      const tail = sp.toString();
      return `${base}/agents${tail ? `?${tail}` : ""}`;
    }
    case "agent-profile": {
      const sp = new URLSearchParams();
      if (key) sp.set("k", key);
      if (opts?.slice) sp.set("slice", opts.slice);
      if (opts?.team) sp.set("team", opts.team);
      if (opts?.timeRange) sp.set("tr", opts.timeRange);
      if (opts?.rangeMode) sp.set("rm", opts.rangeMode);
      if (
        opts?.timeRange === "custom" &&
        opts?.customStart &&
        opts?.customEnd
      ) {
        sp.set("start", opts.customStart);
        sp.set("end", opts.customEnd);
      }
      if (opts?.focusedMeasureId && opts?.focusedLabelName) {
        sp.set("mid", opts.focusedMeasureId);
        sp.set("ln", opts.focusedLabelName);
      }
      const tail = sp.toString();
      return `${base}/agents/${slug ?? ""}${tail ? `?${tail}` : ""}`;
    }
    case "call-detail": {
      const sp = new URLSearchParams();
      if (opts?.from) sp.set("from", opts.from);
      const tail = sp.toString();
      return `${base}/calls/${encodeURIComponent(opts?.callId ?? "")}${tail ? `?${tail}` : ""}`;
    }
    case "users":
      return `${base}/users`;
    case "manage-questions":
      return `${base}/manage-questions`;
    case "communities":
      return `${base}/communities`;
    case "teams":
      return `${base}/teams`;
    case "assessments":
      return `${base}/assessments`;
    case "assessment-edit":
      // ``slug`` is the assessment id when editing, empty when creating a new
      // one (the `+ New assessment` button); AssessmentEditView reads it.
      return `${base}/assessment-edit/${slug ?? ""}`;
    case "assessment-view":
      // ``slug`` is the assessment id; AssessmentReadOnlyView reads it.
      return `${base}/assessment-view/${slug ?? ""}`;
    case "search":
      return `${base}/search`;
    case "roundtable":
      return `${base}/roundtable`;
    case "generate-scenarios": {
      const sp = new URLSearchParams();
      if (opts?.team) sp.set("team", opts.team);
      const tail = sp.toString();
      return `${base}/generate-scenarios${tail ? `?${tail}` : ""}`;
    }
    case "how-to-use":
      return `${base}/how-to-use`;
  }
}

// Task #391 — build an agent-profile URL for opening in a new tab.
// The custom date window (``start``/``end``) lives in the current page
// URL whenever the dashboard is in the "custom" range, so we read it
// from there instead of threading ``customRange`` through every
// component that renders an agent row. ``timeRange`` still gates whether
// those params are written (see the agent-profile case in ``buildUrl``).
export function agentProfileHref(
  slug: string,
  key: string,
  opts: {
    slice?: string | null;
    team?: string | null;
    timeRange?: TimeRange | null;
    rangeMode?: RangeMode | null;
    focusedMeasureId?: string | null;
    focusedLabelName?: string | null;
  },
): string {
  let customStart: string | null = null;
  let customEnd: string | null = null;
  if (opts.timeRange === "custom" && typeof window !== "undefined") {
    const cur = new URLSearchParams(window.location.search);
    customStart = cur.get("start");
    customEnd = cur.get("end");
  }
  return buildUrl("agent-profile", slug, key, {
    ...opts,
    customStart,
    customEnd,
  });
}

// Build the value stored in the `from` query param so the call-detail Back
// button can return to the originating agent profile with its slice / team
// filters intact. Encoded as `slug|agentKey|slice|team` so it's compact and
// trivially parseable. Empty fields are kept so position is stable.
export function encodeAgentFrom(
  slug: string | null,
  agentKey: string | null,
  slice: string | null,
  team: string | null,
): string {
  return [slug ?? "", agentKey ?? "", slice ?? "", team ?? ""]
    .map((s) => encodeURIComponent(s))
    .join("|");
}

export function decodeAgentFrom(value: string | null): {
  slug: string | null;
  agentKey: string | null;
  slice: string | null;
  team: string | null;
} | null {
  if (!value) return null;
  const parts = value.split("|");
  if (parts.length !== 4) return null;
  const [slug, agentKey, slice, team] = parts.map((s) => {
    try {
      return decodeURIComponent(s);
    } catch {
      return s;
    }
  });
  if (!slug) return null;
  return {
    slug,
    agentKey: agentKey || null,
    slice: slice || null,
    team: team || null,
  };
}
